/**
 * Pre-order Quick Add helpers:
 * - Sync availability date / payment from ReStock when available
 * - Toggle preorder UI by selected variant (Charcoal = preorder, Latte = normal ATC)
 * - Keep theme locale copy (EN / DE / FR) — do not overwrite with English ReStock strings
 */
(function () {
  const DEFAULT_AVAILABILITY_DATE = '2026-09-10';
  const DEFAULT_PAYMENT_TEXT = 'Full payment at checkout';
  function getSdkConfig() {
    const fromRestock = window._ReStockConfig && window._ReStockConfig._metafields && window._ReStockConfig._metafields.sdkConfig;
    if (fromRestock) return fromRestock;
    if (window.metafieldConfigData) return window.metafieldConfigData;
    return null;
  }

  function getLocale(block) {
    const fromBlock = block && block.getAttribute('data-locale');
    const locale =
      fromBlock ||
      (window.Shopify && window.Shopify.locale) ||
      (document.documentElement && document.documentElement.lang) ||
      'en';
    return String(locale).split('-')[0].toLowerCase();
  }

  /* Match cart: always show ISO date (YYYY-MM-DD) */
  function formatAvailabilityDate(isoDate) {
    return isoDate || DEFAULT_AVAILABILITY_DATE;
  }

  function getAvailabilityDate(sellingPlanId) {
    if (!sellingPlanId) return DEFAULT_AVAILABILITY_DATE;
    const cfg = getSdkConfig();
    const map = cfg && cfg.description_availability_config;
    const entry = map && map[String(sellingPlanId)];
    return (entry && entry.config && entry.config.date) || DEFAULT_AVAILABILITY_DATE;
  }

  function getPaymentText(sellingPlanId, fallbackText) {
    // Theme Liquid already localized the payment copy — prefer it.
    if (fallbackText) return fallbackText;

    const cfg = getSdkConfig();
    const billing = cfg && cfg.selling_plans_billing_settings && cfg.selling_plans_billing_settings[String(sellingPlanId)];
    const lang = getLocale();
    const preOrderUi =
      window._ReStockConfig &&
      window._ReStockConfig._metafields &&
      window._ReStockConfig._metafields.preOrderConfig &&
      window._ReStockConfig._metafields.preOrderConfig.product_page &&
      window._ReStockConfig._metafields.preOrderConfig.product_page.ui_contents;
    const contents = preOrderUi && (preOrderUi[lang] || preOrderUi.en);

    if (billing && billing.payment_type === 'FULL' && contents && contents.billing_full_payment_text) {
      return contents.billing_full_payment_text;
    }
    if (contents && contents.billing_full_payment_text) {
      return contents.billing_full_payment_text;
    }
    return DEFAULT_PAYMENT_TEXT;
  }

  function buildAvailabilityLine(block, isoDate) {
    const displayDate = formatAvailabilityDate(isoDate);
    const template = block.getAttribute('data-availability-line-template');
    if (template) return template.replace('__DATE__', displayDate);
    return displayDate;
  }

  function updatePreorderCopy(block, planId) {
    const isoDate = getAvailabilityDate(planId);
    const fallbackPayment = block.getAttribute('data-default-payment');
    const paymentLine =
      block.getAttribute('data-payment-line') || getPaymentText(planId, fallbackPayment);

    const availabilityRow = block.querySelector('[data-preorder-availability-line], [data-preorder-availability-row]');
    const paymentRow = block.querySelector('[data-preorder-payment-line], [data-preorder-payment-row]');
    const dateEl = block.querySelector('[data-preorder-availability-date]');
    const paymentEl = block.querySelector('[data-preorder-payment]');

    if (availabilityRow && !dateEl) {
      availabilityRow.textContent = buildAvailabilityLine(block, isoDate);
    } else if (dateEl) {
      dateEl.textContent = formatAvailabilityDate(isoDate);
    }

    if (paymentRow && !paymentEl) {
      paymentRow.textContent = paymentLine;
    } else if (paymentEl) {
      paymentEl.textContent = getPaymentText(planId, fallbackPayment);
    }
  }

  function parseVariantIds(value) {
    if (!value) return [];
    return String(value)
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  function isPreorderVariant(variantId, variantIdsAttr) {
    const ids = parseVariantIds(variantIdsAttr);
    if (!ids.length) return false;
    return ids.includes(String(variantId));
  }

  function setInputEnabled(input, enabled) {
    if (!input) return;
    if (enabled) {
      input.disabled = false;
      input.removeAttribute('disabled');
    } else {
      input.disabled = true;
      input.setAttribute('disabled', 'disabled');
    }
  }

  function updateButtonLabel(scope, isPreorder) {
    scope.querySelectorAll('buy-buttons[data-preorder-variant-ids]').forEach((buyButtons) => {
      const button = buyButtons.querySelector('button[type="submit"]');
      if (!button) return;
      const atc = buyButtons.getAttribute('data-atc-label') || 'Add to cart';
      const preorder = buyButtons.getAttribute('data-preorder-label') || 'Pre-order';
      button.textContent = isPreorder ? preorder : atc;
      if (isPreorder) {
        buyButtons.setAttribute('data-preorder', '');
      } else {
        buyButtons.removeAttribute('data-preorder');
      }
    });
  }

  function applyVariantState(root, variantId) {
    if (!root || variantId == null) return;

    root.querySelectorAll('[data-product-preorder]').forEach((block) => {
      const active = isPreorderVariant(variantId, block.getAttribute('data-preorder-variant-ids'));
      block.hidden = !active;
      if (active) {
        updatePreorderCopy(block, block.getAttribute('data-selling-plan-id'));
      }
    });

    const scopes = root.querySelectorAll('form, product-rerender, quick-buy-drawer, .quick-buy-drawer__info');
    const lists = scopes.length ? scopes : [root];

    lists.forEach((scope) => {
      const planInput = scope.querySelector('[data-preorder-selling-plan]');
      const dateInput = scope.querySelector('[data-preorder-availability-input]');
      const paymentInput = scope.querySelector('[data-preorder-payment-input]');
      const idsAttr =
        (planInput && planInput.getAttribute('data-preorder-variant-ids')) ||
        (dateInput && dateInput.getAttribute('data-preorder-variant-ids')) ||
        (scope.querySelector('[data-preorder-variant-ids]') &&
          scope.querySelector('[data-preorder-variant-ids]').getAttribute('data-preorder-variant-ids'));

      const active = isPreorderVariant(variantId, idsAttr);
      setInputEnabled(planInput, active);
      setInputEnabled(dateInput, active);
      setInputEnabled(paymentInput, active);

      if (active && dateInput) {
        const planId = planInput && planInput.value;
        dateInput.value = getAvailabilityDate(planId);
      }
      if (active && paymentInput) {
        const planId = planInput && planInput.value;
        const fallbackPayment =
          paymentInput.getAttribute('data-default-payment') ||
          (scope.querySelector('[data-product-preorder]') &&
            scope.querySelector('[data-product-preorder]').getAttribute('data-default-payment'));
        paymentInput.value = getPaymentText(planId, fallbackPayment);
      }

      updateButtonLabel(scope, active);
    });
  }

  function syncRoot(root) {
    if (!root || !root.querySelectorAll) return;

    root.querySelectorAll('[data-product-preorder]').forEach((block) => {
      if (block.hidden) return;
      updatePreorderCopy(block, block.getAttribute('data-selling-plan-id'));
    });

    root.querySelectorAll('[data-preorder-selling-plan]:not([disabled])').forEach((input) => {
      const planId = input.value;
      const form = input.form || input.closest('form') || root;
      const dateInput = form.querySelector('[data-preorder-availability-input]');
      const paymentInput = form.querySelector('[data-preorder-payment-input]');
      if (dateInput) dateInput.value = getAvailabilityDate(planId);
      if (paymentInput) {
        const fallbackPayment =
          paymentInput.getAttribute('data-default-payment') ||
          (form.querySelector('[data-product-preorder]') &&
            form.querySelector('[data-product-preorder]').getAttribute('data-default-payment'));
        paymentInput.value = getPaymentText(planId, fallbackPayment);
      }
    });
  }

  function onVariantChange(event) {
    const variant = event.detail && event.detail.variant;
    if (!variant || variant.id == null) return;
    const form = event.target;
    const root =
      (form && form.closest && (form.closest('quick-buy-drawer') || form.closest('product-rerender') || form.closest('.quick-buy-drawer__info'))) ||
      form ||
      document;
    applyVariantState(root, variant.id);
  }

  function syncAll() {
    syncRoot(document);
    document.querySelectorAll('quick-buy-drawer, product-rerender, form[is="product-form"]').forEach((root) => {
      const idInput = root.querySelector?.('input[name="id"]') || (root.matches?.('form') ? root.querySelector('input[name="id"]') : null);
      if (idInput && idInput.value) applyVariantState(root, idInput.value);
    });
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (
          node.matches?.('[data-product-preorder], [data-preorder-selling-plan], quick-buy-drawer, product-rerender, buy-buttons') ||
          node.querySelector?.('[data-product-preorder], [data-preorder-selling-plan], buy-buttons[data-preorder-variant-ids]')
        ) {
          syncRoot(node);
          const idInput = node.querySelector?.('input[name="id"]');
          if (idInput && idInput.value) applyVariantState(node, idInput.value);
        }
      }
    }
  });

  function start() {
    document.addEventListener('variant:change', onVariantChange);
    syncAll();
    observer.observe(document.documentElement, { childList: true, subtree: true });
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      syncAll();
      if (getSdkConfig() || attempts >= 40) clearInterval(timer);
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', syncAll);
})();
